import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Checkbox, FileDrop, Input, TextArea } from '../../uiKit/inputs';
import ButtonFulfilled from '../../uiKit/buttons/fulfilled';
import Title from '../../uiKit/typography/title';
import Paragraph from '../../uiKit/typography/paragraph';

import { useSendCandidateProposalMutation } from '../../redux/api/common';
import { useTranslation } from 'react-i18next';
import { useGoogleCaptcha } from '../../utils/hooks/useGoogleCaptcha';
import { fileToBase64 } from '../../utils/fileToBase64';
import { deleteFile, truncateFilesBySizeLimit } from '../../utils/filesHelpers';

import { ReactComponent as CrossIcon } from '../../assets/admin/icons/system/cross.svg';
import Label from './label';
import { validateBeforeInput } from '../../utils/inputHelpers';
import { validateEmail } from '../../utils/email';

function generateFieldsValidity(data) {
  return Object.fromEntries(Object.entries(data).map(entry => [entry[0], { valid: true }]));
}

const authorFields = [
  { field: 'authorFirstName', serverField: 'firstName' },
  { field: 'authorLastName', serverField: 'lastName' },
  { field: 'authorEmail', serverField: 'email' },
  { field: 'authorPhone', serverField: 'phone' },
  { field: 'isCompanyEmployee', serverField: 'isCompanyEmployee' }
];

const defaultData = {
  firstName: '',
  lastName: '',
  linkedIn: '',
  knowAboutRecommendation: null,
  files: [],

  authorFirstName: '',
  authorLastName: '',
  authorEmail: '',
  authorPhone: '',
  isCompanyEmployee: null,

  processingDataConfirmed: false,
};

export default function ReferralForm() {
  const { t } = useTranslation('common');
  const [sendProposal, { isLoading }] = useSendCandidateProposalMutation();
  const [success, setSuccess] = useState(null);
  const executeWithToken = useGoogleCaptcha(true);
  const formRef = useRef(null);
  const fileDropRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [data, setData] = useState(() => defaultData);
  const [fieldsValidity, setFieldsValidity] = useState(() => generateFieldsValidity(data));

  const onDragStart = useCallback(() => {
    setDragging(true);
  }, [setDragging]);

  const onDragEnd = useCallback((e) => {
    setDragging(false);
  }, [setDragging]);

  const onChange = (e) => {
    const field = e.target.name;
    let value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;

    if (!fieldsValidity[field].valid) setFieldsValidity(prev => ({
      ...prev,
      [field]: { ...prev[field], valid: true }
    }));
    setData(prev => ({ ...prev, [field]: value }));
  };

  const onChangeRadioGroup = (e) => {
    const field = e.target.name;
    const value = Boolean(Number(e.target.value));

    if (!fieldsValidity[field].valid) setFieldsValidity(prev => ({
      ...prev,
      [field]: { ...prev[field], valid: true }
    }));
    setData(prev => ({ ...prev, [field]: value }));
  }

  const onFilePick = useCallback(async (e) => {
    const field = e.target.name;

    let files = e.target.files;

    if (!files) {
      return;
    }

    files = truncateFilesBySizeLimit(files, 10485760, fileDropRef.current?.inputRef.current); //10MB

    const file = files[0];
    if (!file) {
      console.error('Max size exceeded');
      return;
    }

    const base64 = await fileToBase64(file);
    if (base64.error) {
      console.error(base64.error);
    } else {
      if (!fieldsValidity[field].valid) setFieldsValidity(prev => ({
        ...prev,
        [field]: { ...prev[field], valid: true }
      }));
      setData(prev => ({
        ...prev,
        [field]: [{
          filename: file.name,
          content: base64.result,
          encoding: 'base64'
        }]
      }));
    }
  }, [fieldsValidity]);

  const onFileDelete = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    setData(prev => ({ ...prev, files: prev.files.filter((file, pos) => pos !== index) }));
    deleteFile(fileDropRef.current?.inputRef.current.files || [], index, fileDropRef.current?.inputRef.current);
  };

  const validate = () => {
    const inputs = formRef.current.elements;
    let isValid = true;

    for (let field in fieldsValidity) {
      if (field === 'authorEmail') {
        const isEmailValid = validateEmail(data[field]);
        if (!isEmailValid) {
          isValid = false;
          setFieldsValidity(prev => ({ ...prev, [field]: { ...prev[field], valid: false } }));
        }
      }

      if (inputs[field] instanceof RadioNodeList) {
        if (Array.from(inputs[field]).every(input => !input.validity.valid)) {
          setFieldsValidity(prev => ({ ...prev, [field]: { ...prev[field], valid: false } }));
          isValid = false;
        }
      } else {
        if (!inputs[field].validity.valid) {
          setFieldsValidity(prev => ({ ...prev, [field]: { ...prev[field], valid: false } }));
          isValid = false;
        }
      }
    }

    return isValid
  };

  const captchaCallback = async (token) => {
    try {
      let sendData = {
        proposedBy: {},
      };
      for (let field in data) {
        if (field === 'processingDataConfirmed') continue;

        const authorField = authorFields.find(({ field: f }) => f === field);
        if (authorField) {
          sendData.proposedBy[authorField.serverField] = data[field];
        } else {
          sendData[field] = data[field];
        }
      }

      await sendProposal({ ...sendData, captcha: token }).unwrap()
        .then((res) => {
          if (res.success) {
            setSuccess(true);
            setData(defaultData);
          } else {
            console.error(res.message);
            setSuccess(false);
          }
        });
    } catch (e) {
      console.error(e);
      setSuccess(false);
    }
  };

  const checkPhoneFormat = (e) => {
    validateBeforeInput(e, /^\+?[0-9]{0,15}$/);
  }

  const submit = (e) => {
    e.preventDefault();
    const isValid = validate();
    if (!isValid) return;

    executeWithToken(captchaCallback, 'submitReferralForm');
  };

  return (
    <div className={'bg-rose p-6 md:p-24 rounded-2xl'}>
      {!isLoading && success
        ? <div className={'flex flex-col items-center justify-center gap-4 animate-fadeIn'}>
          <Title className={'text-4xl md:text-4xl !leading-tight'}>
            {t('titles.thank_you')}
          </Title>
          <Paragraph className={'text-center'}>
            {t('descriptions.message_sent')}
          </Paragraph>
        </div>
        : <form ref={formRef} onSubmit={submit} className={'w-full flex flex-col gap-6 animate-fadeIn'} noValidate>
          <Title className={'!text-3xl !leading-tight'}>
            {t('titles.referral_form')}
          </Title>

          <div className={'flex flex-col gap-10'}>
            <div className={'flex flex-col gap-2'}>
              <Title className={'!text-xl !leading-tight !text-left'}>
                {t('titles.referral_form_your_info')}
              </Title>
              <div className={'grid grid-cols-2 gap-x-2 gap-y-6'}>
                <Label label={t('labels.referral_form.first_name')}
                       className={'[&>span]:text-gray-700 col-span-2 md:col-span-1 flex flex-col gap-1.5'} required>
                  <Input name={'authorFirstName'} value={data.authorFirstName} onChange={onChange}
                         className={`!h-14 ${!fieldsValidity.authorFirstName.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         placeholder={t('placeholders.first_name')} maxLength={30} required />
                </Label>

                <Label label={t('labels.referral_form.last_name')}
                       className={'[&>span]:text-gray-700 col-span-2 md:col-span-1 flex flex-col gap-1.5'} required>
                  <Input name={'authorLastName'} value={data.authorLastName} onChange={onChange}
                         className={`!h-14 ${!fieldsValidity.authorLastName.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         placeholder={t('placeholders.last_name')} maxLength={30} required />
                </Label>

                <Label label={t('labels.referral_form.email')}
                       className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required>
                  <Input name={'authorEmail'} value={data.authorEmail} onChange={onChange}
                         placeholder={t('placeholders.email')}
                         className={`!h-14 ${!fieldsValidity.authorEmail.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         type={'email'} maxLength={30} required />
                </Label>

                <Label label={t('labels.referral_form.phone')}
                       className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required>
                  <Input name={'authorPhone'} value={data.authorPhone} onChange={onChange}
                         className={`!h-14 ${!fieldsValidity.authorPhone.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         minLength={6} maxLength={16} onBeforeInput={checkPhoneFormat} required />
                </Label>

                <div className={'col-span-2 flex flex-col gap-1.5'}>
                  <Label label={t('labels.referral_form.is_company_employee')}
                         className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required />
                  <div className={'flex gap-2'}>
                    <label className={'flex gap-2'}>
                      <Checkbox type={'radio'} name={'isCompanyEmployee'} value={1} checked={!!data.isCompanyEmployee}
                                onChange={onChangeRadioGroup}
                                className={!fieldsValidity.isCompanyEmployee.valid ? 'before:!border-red-400 animate-errorShake' : ''}
                                required />
                      <span>
                        {t('labels.referral_form.yes')}
                      </span>
                    </label>

                    <label className={'flex gap-2'}>
                      <Checkbox type={'radio'} name={'isCompanyEmployee'} value={0}
                                checked={data.isCompanyEmployee !== null && !data.isCompanyEmployee}
                                onChange={onChangeRadioGroup}
                                className={!fieldsValidity.isCompanyEmployee.valid ? 'before:!border-red-400 animate-errorShake' : ''}
                                required />
                      <span>
                        {t('labels.referral_form.no')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>


            <div className={'flex flex-col gap-2'}>
              <Title className={'!text-xl !leading-tight !text-left'}>
                {t('titles.referral_form_referral_info')}
              </Title>
              <div className={'grid grid-cols-2 gap-x-2 gap-y-6'}>
                <Label label={t('labels.referral_form.first_name')}
                       className={'[&>span]:text-gray-700 col-span-2 md:col-span-1 flex flex-col gap-1.5'} required>
                  <Input name={'firstName'} value={data.firstName} onChange={onChange}
                         className={`!h-14 ${!fieldsValidity.firstName.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         placeholder={t('placeholders.first_name')} maxLength={30} required />
                </Label>

                <Label label={t('labels.referral_form.last_name')}
                       className={'[&>span]:text-gray-700 col-span-2 md:col-span-1 flex flex-col gap-1.5'} required>
                  <Input name={'lastName'} value={data.lastName} onChange={onChange}
                         className={`!h-14 ${!fieldsValidity.lastName.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         placeholder={t('placeholders.last_name')} maxLength={30} required />
                </Label>

                <Label label={t('labels.referral_form.linked_in')}
                       className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required>
                  <Input name={'linkedIn'} value={data.linkedIn} onChange={onChange}
                         placeholder={'https://linkedin.com/in/username'}
                         className={`!h-14 ${!fieldsValidity.linkedIn.valid ? '!border-red-400 animate-errorShake' : ''}`}
                         maxLength={30} pattern={'^https:\\/\\/.+?\(linkedin\).+'} required />
                </Label>

                <div className={'col-span-2 flex-1'}>
                  <Label label={t('labels.referral_form.cv')}
                         className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required />
                  <FileDrop name={'files'} onDrop={onDragEnd} onDragLeave={onDragEnd} onDragOver={onDragStart}
                            onChange={onFilePick} accept={'.pdf, .doc, .docx'} ref={fileDropRef}
                            label={data.files.length && !dragging
                              ? <div
                                className={'w-full flex flex-wrap justify-center gap-1.5 m-2 p-2'}>
                                {data.files.map((file, index) => (
                                  <div key={index}
                                       className={'btn__outlined group max-w-full py-0.5 px-1.5 before:rounded-md before:p-px pointer-events-auto break-words'}>
                                    <CrossIcon onClick={(e) => onFileDelete(e, index)}
                                               className={'absolute  top-0 right-0 bg-black   opacity-0 group-hover:opacity-100 pointer-events-auto rounded-full p-px  w-3 h-3 translate-y-[-50%] translate-x-[50%] cursor-pointer'} />
                                    <span>{file.filename}</span>
                                  </div>
                                ))}
                              </div>
                              : <div className={'text-center text-xs md:text-base text-gray-400 whitespace-pre'}>
                                <span className={`${dragging ? 'hidden' : ''}`}><span
                                  className={`!text-gradient`}>{t('placeholders.uploadResumeFile_0')}</span>
                              <span>{t('placeholders.uploadResumeFile_1')}</span></span>
                                {dragging
                                  ? <p className={'first-letter:uppercase'}>{t('placeholders.uploadResumeFile_2')}</p>
                                  : <span>{t('placeholders.uploadResumeFile_2')}</span>}
                              </div>}
                            className={`absolute cursor-pointer shadow-sm !border-solid bg-white ${!fieldsValidity.files.valid ? '!border-red-400 animate-errorShake' : '!border-[#D0D5DD]'}`}
                            wrapperClassName={'w-auto h-auto !min-h-[5rem]'}
                            labelClassName={'relative'}
                            required={!data.files.length}
                  />
                </div>

                <div className={'col-span-2 flex flex-col gap-1.5'}>
                  <Label label={t('labels.referral_form.candidate_know_about_recommendation')}
                         className={'[&>span]:text-gray-700 col-span-2 flex flex-col gap-1.5'} required />
                  <div className={'flex gap-2'}>
                    <label className={'flex gap-2'}>
                      <Checkbox type={'radio'} name={'knowAboutRecommendation'} value={1}
                                checked={!!data.knowAboutRecommendation}
                                onChange={onChangeRadioGroup}
                                className={!fieldsValidity.knowAboutRecommendation.valid ? 'before:!border-red-400 animate-errorShake' : ''}
                                required />
                      <span>
                        {t('labels.referral_form.yes')}
                      </span>
                    </label>

                    <label className={'flex gap-2'}>
                      <Checkbox type={'radio'} name={'knowAboutRecommendation'} value={0}
                                checked={data.knowAboutRecommendation !== null && !data.knowAboutRecommendation}
                                onChange={onChangeRadioGroup}
                                className={!fieldsValidity.knowAboutRecommendation.valid ? 'before:!border-red-400 animate-errorShake' : ''}
                                required />
                      <span>
                        {t('labels.referral_form.no')}
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <Label label={t('labels.referral_form.confirm_to_process_personal_data')}
                   className={'[&>span]:text-gray-700 col-span-2 md:col-span-1 flex flex-row-reverse !justify-end gap-1.5'}>
              <Checkbox name={'processingDataConfirmed'}
                        checked={!!data.processingDataConfirmed}
                        onChange={onChange}
                        className={!fieldsValidity.processingDataConfirmed.valid ? 'before:!border-red-400 animate-errorShake' : ''}
                        required />
            </Label>
          </div>

          <div
            className={'col-span-2 flex m-auto mt-6 flex-col justify-center items-center'}>
            <ButtonFulfilled disabled={Object.values(fieldsValidity).some(value => !value.valid) || !data.processingDataConfirmed || isLoading} type={'submit'}
                             className={'text-lg text-white font-semibold px-6 !py-4 before:transition-all disabled:grayscale disabled:before:opacity-50 shrink-0'}>
              {t('buttons.submit')}
            </ButtonFulfilled>
            <div className={'relative w-full flex items-start md:items-center justify-center'}>
              {!isLoading && success === false &&
                <span className={`relative animate-appearY text-red-400`}>
            {t('errors.message_error')}
          </span>
              }
            </div>
          </div>

        </form>
      }
    </div>
  );
}
